import React, { useState, useEffect } from 'react';
import { RefreshCw, ExternalLink, ThumbsUp, Heart, Search } from 'lucide-react';

const NewsTab = () => {
  // State
  const [newsArticles, setNewsArticles] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [newsSearchTerm, setNewsSearchTerm] = useState('');
  const [selectedNewsCategory, setSelectedNewsCategory] = useState('all');
  const [selectedNewsSource, setSelectedNewsSource] = useState('all');
  const [newsSortBy, setNewsSortBy] = useState('newest');
  const [newsCategories, setNewsCategories] = useState([]);
  const [newsSources, setNewsSources] = useState([]);
  const [userEngagement, setUserEngagement] = useState({ liked_articles: [], loved_articles: [] });
  const [newsPage, setNewsPage] = useState(1);
  const newsPerPage = 10;

  // Load news sources
  const loadNewsSources = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/news/sources`);
      
      if (response.ok) {
        const data = await response.json();
        setNewsCategories(data.categories || []);
        setNewsSources(data.sources || []);
        console.log('[NEWS] Loaded categories:', data.categories);
        console.log('[NEWS] Loaded sources:', data.sources.length);
      }
    } catch (error) {
      console.error('[NEWS] Failed to load news sources:', error);
    }
  };

  // Load news articles
  const loadNewsArticles = async () => {
    setLoadingNews(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const token = localStorage.getItem('beatseek_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const selectedSource = newsSources.find(s => s.id === selectedNewsSource);
      const sourceName = selectedSource ? selectedSource.name : '';
      const sourceParam = selectedNewsSource !== 'all' ? `&source=${encodeURIComponent(sourceName)}` : '';
      const searchParam = newsSearchTerm ? `&search=${encodeURIComponent(newsSearchTerm)}` : '';
      const sortParam = `&sort=${newsSortBy}`;
      const refreshParam = '';
      
      const response = await fetch(
        `${BACKEND_URL}/api/news/feed?limit=100${sourceParam}${searchParam}${sortParam}${refreshParam}`,
        { headers }
      );
      
      if (response.ok) {
        const data = await response.json();
        setNewsArticles(data.articles || []);
        if (data.user_engagement) {
          setUserEngagement(data.user_engagement);
        }
      }
    } catch (error) {
      console.log('Failed to load news articles');
    } finally {
      setLoadingNews(false);
    }
  };

  // Handle like article
  const handleLikeArticle = async (articleId, event) => {
    event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to like articles');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/news/${articleId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setUserEngagement(prev => ({
          ...prev,
          liked_articles: prev.liked_articles?.includes(articleId)
            ? prev.liked_articles.filter(id => id !== articleId)
            : [...(prev.liked_articles || []), articleId]
        }));
      }
    } catch (error) {
      console.error('Failed to like article:', error);
    }
  };

  // Handle love article
  const handleLoveArticle = async (articleId, event) => {
    event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to love articles');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/news/${articleId}/love`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setUserEngagement(prev => ({
          ...prev,
          loved_articles: prev.loved_articles?.includes(articleId)
            ? prev.loved_articles.filter(id => id !== articleId)
            : [...(prev.loved_articles || []), articleId]
        }));
      }
    } catch (error) {
      console.error('Failed to love article:', error);
    }
  };

  // Load on mount
  useEffect(() => {
    loadNewsArticles();
    loadNewsSources();
  }, [selectedNewsCategory, selectedNewsSource, newsSortBy]);

  // Format number
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  // Pagination
  const paginatedArticles = newsArticles.slice(
    (newsPage - 1) * newsPerPage,
    newsPage * newsPerPage
  );
  const totalPages = Math.ceil(newsArticles.length / newsPerPage);

  return (
    <div className="news-tab" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', maxHeight: '75vh' }}>
      {/* Toolbar */}
      <div className="news-toolbar" style={{
        padding: '1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        background: 'rgba(0, 0, 0, 0.2)',
        flexShrink: 0
      }}>
        {/* Controls row */}
        <div className="news-controls" style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Refresh button */}
          <button
            onClick={() => loadNewsArticles()}
            disabled={loadingNews}
            style={{
              background: 'transparent',
              border: 'none',
              color: loadingNews ? 'rgba(255, 255, 255, 0.5)' : '#00ffff',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: loadingNews ? 'not-allowed' : 'pointer',
              padding: '0.5rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loadingNews) e.currentTarget.style.color = '#ff00ff';
            }}
            onMouseLeave={(e) => {
              if (!loadingNews) e.currentTarget.style.color = '#00ffff';
            }}
          >
            <RefreshCw style={{ width: '0.875rem', height: '0.875rem', animation: loadingNews ? 'spin 1s linear infinite' : 'none' }} />
            {loadingNews ? 'refreshing...' : 'refresh'}
          </button>

          {/* Search input */}
          <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
            <Search style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '1rem',
              height: '1rem',
              color: 'rgba(255, 255, 255, 0.5)'
            }} />
            <input
              type="text"
              placeholder="Search articles..."
              value={newsSearchTerm}
              onChange={(e) => setNewsSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                color: '#fff',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onKeyPress={(e) => { if (e.key === 'Enter') { setNewsPage(1); loadNewsArticles(); } }}
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedNewsCategory}
            onChange={(e) => {
              setSelectedNewsCategory(e.target.value);
              setSelectedNewsSource('all');
              setNewsPage(1);
            }}
            style={{
              padding: '0.75rem 2rem 0.75rem 1rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {newsCategories.map(cat => (
              <option key={cat.id} value={cat.id} style={{ background: '#1a1a2e' }}>
                {cat.name}
              </option>
            ))}
          </select>
          
          {/* Individual Source Filter */}
          <select
            value={selectedNewsSource}
            onChange={(e) => {
              setSelectedNewsSource(e.target.value);
              setNewsPage(1);
            }}
            style={{
              padding: '0.75rem 2rem 0.75rem 1rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <option value="all" style={{ background: '#1a1a2e' }}>Individual Sources</option>
            {newsSources
              .filter(src => selectedNewsCategory === 'all' || src.category === selectedNewsCategory)
              .map(source => (
                <option key={source.id} value={source.id} style={{ background: '#1a1a2e' }}>
                  {source.name}
                </option>
              ))
            }
          </select>

          {/* Sort */}
          <select
            value={newsSortBy}
            onChange={(e) => setNewsSortBy(e.target.value)}
            style={{
              padding: '0.75rem 2rem 0.75rem 1rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <option value="newest" style={{ background: '#1a1a2e' }}>Newest First</option>
            <option value="oldest" style={{ background: '#1a1a2e' }}>Oldest First</option>
          </select>

          {/* Results info — inline with controls (CSS reorders/sizes per breakpoint) */}
          <p className="news-results-info" style={{
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.6)',
            margin: 0,
            flexShrink: 0
          }}>
            {newsSearchTerm ? (
              `Found ${newsArticles.length} article${newsArticles.length !== 1 ? 's' : ''} for "${newsSearchTerm}"`
            ) : (
              `Showing ${paginatedArticles.length} of ${newsArticles.length} articles ${selectedNewsSource !== 'all' ? `from ${newsSources.find(s => s.id === selectedNewsSource)?.name || selectedNewsSource}` : 'from all sources'}`
            )}
          </p>
        </div>
      </div>

      {/* Articles list */}
      <div className="news-articles" style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {loadingNews ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading articles...</p>
          </div>
        ) : paginatedArticles.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>No articles found</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {paginatedArticles.map((article) => {
              const isLiked = userEngagement.liked_articles?.includes(article.id);
              const isLoved = userEngagement.loved_articles?.includes(article.id);

              return (
                <div
                  key={article.id}
                  className="news-article-card"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.8)';
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 0, 255, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => window.open(article.url, '_blank')}
                >
                  {/* Source badge */}
                  <div style={{
                    display: 'inline-block',
                    background: 'rgba(255, 0, 255, 0.15)',
                    color: '#ff00ff',
                    fontSize: '0.7rem',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '0.75rem',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    border: '1px solid rgba(255, 0, 255, 0.3)'
                  }}>
                    {article.source}
                  </div>

                  {/* Title */}
                  <h3 style={{
                    fontWeight: '600',
                    color: '#fff',
                    fontSize: '0.95rem',
                    marginBottom: '0.5rem',
                    lineHeight: '1.4',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {article.title}
                    <ExternalLink size={14} style={{ flexShrink: 0, color: 'rgba(255, 255, 255, 0.5)' }} />
                  </h3>

                  {/* Description */}
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    lineHeight: '1.5',
                    marginBottom: '0.75rem'
                  }}>
                    {article.description}
                  </p>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <button
                      onClick={(e) => handleLikeArticle(article.id, e)}
                      style={{
                        background: isLiked ? 'rgba(0, 255, 255, 0.2)' : 'rgba(0, 255, 255, 0.1)',
                        border: `1px solid ${isLiked ? 'rgba(0, 255, 255, 0.6)' : 'rgba(0, 255, 255, 0.3)'}`,
                        color: '#00ffff',
                        padding: '0.3rem 0.6rem',
                        borderRadius: '0.4rem',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <ThumbsUp size={12} fill={isLiked ? '#00ffff' : 'none'} />
                      Like
                    </button>
                    <button
                      onClick={(e) => handleLoveArticle(article.id, e)}
                      style={{
                        background: isLoved ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 0, 255, 0.1)',
                        border: `1px solid ${isLoved ? 'rgba(255, 0, 255, 0.6)' : 'rgba(255, 0, 255, 0.3)'}`,
                        color: '#ff00ff',
                        padding: '0.3rem 0.6rem',
                        borderRadius: '0.4rem',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Heart size={12} fill={isLoved ? '#ff00ff' : 'none'} />
                      Love
                    </button>
                    <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.5)', marginLeft: 'auto' }}>
                      {new Date(article.published_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination — inside the scrolling container so it scrolls with the articles */}
        {!loadingNews && newsArticles.length > newsPerPage && (
          <div className="news-pagination" style={{
            padding: '1rem',
            marginTop: '0.75rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '0.5rem'
          }}>
            <button
              onClick={() => setNewsPage(prev => Math.max(1, prev - 1))}
              disabled={newsPage === 1}
              style={{
                padding: '0.5rem 1rem',
                background: newsPage === 1 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 0, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.5rem',
                color: newsPage === 1 ? 'rgba(255, 255, 255, 0.3)' : '#fff',
                fontSize: '0.875rem',
                cursor: newsPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
              Page {newsPage} of {totalPages}
            </span>
            <button
              onClick={() => setNewsPage(prev => Math.min(totalPages, prev + 1))}
              disabled={newsPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                background: newsPage === totalPages ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 0, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.5rem',
                color: newsPage === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#fff',
                fontSize: '0.875rem',
                cursor: newsPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsTab;
